import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import useAutoRunOnFilterChange from '@hooks/useAutoRunOnFilterChange'
import useLatestRun from '@hooks/useLatestRun'
import dayjs from 'dayjs'
import accountingReportService, { accountingEndpoint, accountingAttachFormats } from '@api/v1/accountingReportService'
import reportJobService from '@api/v1/reportJobService'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { dateField, choiceField, boolField } from '@components/reports/specializedFilterFields'
import ReportDataGrid from '@components/reports/ReportDataGrid'
import { withAliasKeys } from '@components/reports/aliasedGridColumns'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import { describeFilters, dateRangePart, basisPart } from '@components/reports/reportFilterSummary'
import { trialBalanceSavedCriteria } from '@components/reports/savedRunCriteria'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import { registerHref } from './accountLinks'

const fmtMoney = (v) => {
  const n = Number(v ?? 0)
  if (n === 0) return ''
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// displayValue is injected as HTML by the universal grid — escape raw values.
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// CC-49594: publish the grid's export view to the surrounding shell so it can
// offer the client-built PDF. The context provider lives inside
// SpecializedReportShell, so this wrapper must render as a child of the shell.
const PublishedGrid = ({ report, storageKey, pageStateRef, initialPageState }) => {
  const exportCtx = useReportExportView()
  const exportViewRef = useRef(null)
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => exportViewRef.current?.()
    exportCtx.onPublish?.()
  }, [exportCtx])
  // CC-49906: the page's own "Show zero balances" filter (server-side) is the
  // survivor — suppress the grid's generic "Hide $0" toggle so the two don't
  // duplicate each other.
  // CC-49732: forward the saved-view page-state plumbing (see BalanceSheetReport).
  return (
    <ReportDataGrid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportViewRef}
      showZeroToggle={false}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
    />
  )
}

const makeDefaults = () => ({
  endDate: dayjs().format('YYYY-MM-DD'),
  isCashBasis: true,
  showZeroBal: false,
  sortBy: 'Category',
})

// `sortDirection` has no control and must survive every edit.
const toWire = (f) => ({
  endDate: f.endDate,
  isCashBasis: f.isCashBasis,
  showZeroBal: f.showZeroBal,
  sortBy: f.sortBy,
  sortDirection: 'ASC',
})

// A trial balance is a point-in-time statement, so As Of is a single date, not a
// range. Basis and Sort By are mutually exclusive choices → RADIO (single-select);
// Cash-vs-Accrual in particular must never be multi-selectable.
const FIELDS = [
  dateField({ key: 'asOf', label: 'As Of', valueKey: 'endDate' }),
  choiceField({
    key: 'basis', label: 'Basis', valueKey: 'isCashBasis',
    options: [{ value: 'cash', label: 'Cash' }, { value: 'accrual', label: 'Accrual' }],
    encode: (v) => (v ? 'cash' : 'accrual'),
    decode: (s) => s === 'cash',
  }),
  choiceField({
    key: 'sortBy', label: 'Sort By', valueKey: 'sortBy',
    options: [
      { value: 'Category', label: 'Category' },
      { value: 'AccountName', label: 'Account Name' },
      { value: 'AccountNumber', label: 'Account Number' },
    ],
  }),
  boolField({ key: 'showZeroBal', label: 'Show zero balances', valueKey: 'showZeroBal' }),
]

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const TrialBalanceReport = ({ reportId, name = 'Trial Balance', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // CC-50029: state the as-of date + basis in the exported header block.
  const filtersSummary = useMemo(
    () => describeFilters(
      dateRangePart(null, filters.endDate),
      basisPart(filters.isCashBasis),
      filters.showZeroBal ? 'Including zero balances' : null,
      filters.sortBy ? `Sorted by ${filters.sortBy}` : null,
    ),
    [filters]
  )
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria. Spinner immediately when reopening a saved result.
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  const [report, setReport] = useState(null)
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Spec 185 (CC-53435): the reopened job's own stored params — read ONLY to state the
  // saved run's criteria on the documents. Never seeded into `filters`: that would fire
  // useAutoRunOnFilterChange and replace the saved figures with a fresh run.
  const [savedParams, setSavedParams] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // CC-49839: bump to remount the grid after a saved view writes layout prefs.
  const [gridKey, setGridKey] = useState(0)
  // CC-49732: grid publishes quick-filters + toggles here; appliedGridStateRef
  // carries a restored view's state into the remounted grid (mount-only read).
  const pageStateRef = useRef(null)
  const appliedGridStateRef = useRef(null)

  const params = useMemo(() => toWire(filters), [filters])

  const runReport = useCallback(() => {
    if (!filters.endDate) {
      // Spec 131: the refusal also retires a run still in flight, so an older
      // statement cannot land under "End date is required."
      invalidateRun({ error: 'End date is required.' })
      return undefined
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => { setSavedAsOf(st?.createdAt || null); setSavedParams(st?.savedParams ?? null); setReport(r?.data?.report || null) }
      }
      const data = await accountingReportService.trialBalance.run(params, { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setReport(data?.report || null) }
    }, { criteria: { filtersSummary } })
  }, [params, filters.endDate, reportId, startRun, invalidateRun, filtersSummary])

  // CC-49835: first load + re-run whenever a filter changes (debounced so the
  // date input doesn't fire a fetch per keystroke). The Run button stays for
  // an explicit re-run of the same filters.
  useAutoRunOnFilterChange(runReport, { ...params, endDate: filters.endDate })

  // CC-49839: applying a saved view restores the run filters (the auto-run
  // effect re-fetches) and remounts the grid so it re-reads the saved layout.
  const applyView = useCallback((view) => {
    if (view?.filters && Object.keys(view.filters).length) {
      setFilters((f) => ({ ...f, ...view.filters }))
    }
    // CC-49732: v2 views carry the grid's page state; v1 views have none.
    appliedGridStateRef.current = view?.v >= 2 ? (view.pageState?.grid || null) : null
    setGridKey((k) => k + 1)
  }, [])

  // CC-49348: the legacy endpoint accepts sortBy but does not reliably apply
  // it — sort client-side so Category / Account Name / Account Number all work.
  const SORT_KEYS = {
    Category: (l) => (l.categoryOrder != null ? String(l.categoryOrder).padStart(8, '0') : (l.category ?? '')),
    AccountName: (l) => l.accountName ?? '',
    AccountNumber: (l) => l.centerbaseAccountNumber ?? '',
  }
  const lines = useMemo(() => {
    // CC-49348: the accounting endpoint's line items use accType /
    // debitBalanceNum / creditBalanceNum — the page previously read
    // category/debit/credit, which don't exist, so the Category column and
    // every amount rendered blank (and the report "looked broken", which is
    // what the sorting complaint was really about). Normalize here.
    // Amount fields arrive as formatted strings on some tenants ("1,234.56",
    // "(500.00)") — parse defensively, honoring accounting-style negatives.
    const toAmount = (...cands) => {
      for (const v of cands) {
        if (v == null || v === '') continue
        if (typeof v === 'number' && isFinite(v)) return v
        const str = String(v)
        const n = Number(str.replace(/[^0-9.-]/g, ''))
        if (isFinite(n)) return str.includes('(') ? -Math.abs(n) : n
      }
      return 0
    }
    const raw = (report?.lineItems || []).map((l) => ({
      ...l,
      category: l.category ?? l.accType ?? '',
      categoryOrder: l.accTypeSortOrder,
      debit: toAmount(l.debit, l.debitBalanceNum, l.debitBalance, l.debitBalanceString),
      credit: toAmount(l.credit, l.creditBalanceNum, l.creditBalance, l.creditBalanceString),
    }))
    const key = SORT_KEYS[filters.sortBy] || SORT_KEYS.Category
    return [...raw].sort((a, b) => {
      const cmp = String(key(a)).localeCompare(String(key(b)), undefined, { numeric: true, sensitivity: 'base' })
      if (cmp !== 0) return cmp
      // Stable tie-break: account number, then name.
      return String(a.centerbaseAccountNumber ?? '').localeCompare(String(b.centerbaseAccountNumber ?? ''), undefined, { numeric: true })
        || String(a.accountName ?? '').localeCompare(String(b.accountName ?? ''))
    })
  }, [report, filters.sortBy])  // eslint-disable-line react-hooks/exhaustive-deps
  const totals = useMemo(() => {
    const out = { debit: 0, credit: 0 }
    for (const line of lines) {
      out.debit += Number(line.debit || 0)
      out.credit += Number(line.credit || 0)
    }
    return out
  }, [lines])

  // CB-report shape for the universal flat grid (conventions from AutoReportGrid:
  // dataValue only on numeric columns so sort + SUM recompute stay value-based).
  // The page's own Sort By select feeds pre-sorted `lines` in, so the grid's
  // base row order already reflects it.
  const gridReport = useMemo(() => ({
    // CC-50291: stable hand-written columnIds double as the rename keys.
    columns: withAliasKeys([
      { columnId: 'account', label: 'Account', visible: true, columnAlignment: 'LEFT' },
      { columnId: 'category', label: 'Category', visible: true, columnAlignment: 'LEFT' },
      { columnId: 'debit', label: 'Debit', visible: true, columnAlignment: 'RIGHT' },
      { columnId: 'credit', label: 'Credit', visible: true, columnAlignment: 'RIGHT' },
    ]),
    reportData: lines.map((l) => ({
      isGroup: false,
      dataCells: [
        {
          columnId: 'account',
          // Account number + name combined, styled like the old bespoke table.
          // CC-49837: TB line items carry the real accountId — link the name
          // to the account register (data-cb-nav = SPA click interception).
          // Spec 133: the gap between number and name is a literal SPACE, not
          // `mr-2`. A margin is invisible to every export (they read text), so the
          // PDF / CSV / XLSX / AI all read "6500Administrative Expenses" — 67 of
          // the 72 lines on the live 12/31/2025 cash run.
          displayValue: `${l.centerbaseAccountNumber ? `<span class="text-gray-500">${esc(l.centerbaseAccountNumber)}</span> ` : ''}${
            l.accountId != null
              ? `<a href="${registerHref(l.accountId)}" data-cb-nav class="text-cb-accent-text hover:underline">${esc(l.accountName)}</a>`
              : esc(l.accountName)
          }`,
        },
        { columnId: 'category', displayValue: esc(l.category) },
        { columnId: 'debit', displayValue: fmtMoney(l.debit), dataValue: Number(l.debit || 0) },
        { columnId: 'credit', displayValue: fmtMoney(l.credit), dataValue: Number(l.credit || 0) },
      ],
    })),
    // Total | (blank Category) | debit total | credit total
    reportFooterCells: lines.length > 0 ? [
      { columnId: 'account', displayValue: 'Total' },
      { columnId: 'category', displayValue: '' },
      { columnId: 'debit', displayValue: fmtMoney(totals.debit) },
      { columnId: 'credit', displayValue: fmtMoney(totals.credit) },
    ] : [],
  }), [lines, totals])

  const outOfBalance = lines.length > 0 && Math.abs(totals.debit - totals.credit) > 0.01

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS). `params`
  // above stays the wire boundary — including `sortDirection`, which has no
  // control.
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        // Mirrors accountingReportService.trialBalance.run exactly: an
        // ACCOUNTING job (GET replay + query string), key 'trial-balance'.
        // That key also drives the server's data-security class — trial balance
        // stays per-user because of bank-account security.
        kind: 'accounting',
        accountingKey: 'trial-balance',
        endpoint: (f) => accountingEndpoint('/reports/accounting/trial-balance', toWire(f)),
        filters: (f) => toWire(f),
        // Spec 194: the emailed copy can carry this page's own export.
        attachFormats: accountingAttachFormats('/reports/accounting/trial-balance'),
      }}
      actions={(
        /* CC-49732: getPageState is what makes the saved view v2 and captures
           the grid's quick-filters/toggles so they can be restored. */
        <ReportViewsMenu
          storageKey="report:trial-balance"
          currentFilters={filters}
          onApply={applyView}
          getPageState={() => ({ grid: pageStateRef.current?.() || null })}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar}
      filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      /* Spec 185 (CC-53435): a reopen states the JOB's as-of, basis and zero toggle;
         "Sorted by" is the page's own sort, because the page sorts the lines itself
         (CC-49348). Null → the shell's "not recorded". */
      savedRunFiltersSummary={savedAsOf != null ? trialBalanceSavedCriteria(savedParams, { sortBy: filters.sortBy }) : undefined}
      download={(format, getToken) => accountingReportService.trialBalance.download(params, format, getToken)}
    >
      <PublishedGrid
        key={gridKey}
        report={gridReport}
        storageKey="report:trial-balance"
        pageStateRef={pageStateRef}
        // Pre-existing (CC-49732): a MOUNT-only seed read behind a gridKey bump.
        // Surfaced, not introduced, by spec 131 — the React Compiler lint skipped
        // this whole component while its runReport held a try/finally, which now
        // lives in useLatestRun.
        // eslint-disable-next-line react-hooks/refs
        initialPageState={appliedGridStateRef.current}
      />
      {/* Out-of-balance warning stays page-level, OUTSIDE the universal grid. */}
      {outOfBalance && (
        <div className="mt-2 flex items-center justify-between rounded-lg border border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
          <span className="font-medium">Out of balance!</span>
          <span>Diff: {fmtMoney(Math.abs(totals.debit - totals.credit))}</span>
        </div>
      )}
    </SpecializedReportShell>
  )
}

export default TrialBalanceReport
